/**
 * Timezone-aware date utilities for tenant-scoped calendar boundaries.
 * All functions accept an IANA timezone string (e.g. 'America/Chicago').
 * Fallback is always 'UTC' if tz is falsy.
 */

const safeIANA = (tz) => tz || 'UTC';

// Intl.DateTimeFormat construction costs ~65µs; route boards call the tenant
// date helpers hundreds of times per render. One formatter per (kind, zone) —
// module-level and pure (a formatter holds no per-call state). An invalid zone
// throws in the constructor BEFORE anything is cached, so callers' try/catch
// fallbacks behave exactly as before.
const formatterCache = new Map();
function cachedFormatter(kind, locale, zone, options) {
  const key = `${kind}|${zone}`;
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { timeZone: zone, ...options });
    formatterCache.set(key, f);
  }
  return f;
}
const YMD_OPTIONS = { year: 'numeric', month: '2-digit', day: '2-digit' };

/**
 * Parse a DB timestamp into a correct UTC instant.
 *
 * Some legacy columns are `timestamp without time zone` (e.g. jobs.created_at,
 * invoices.created_at) and store the UTC wall-clock. PostgREST serializes those
 * with NO timezone designator ("2026-06-05T02:06:00"), and `new Date()` parses an
 * offset-less datetime as the VIEWER's browser tz — shifting the instant by the
 * local offset. That made recent rows look "in the future" on activity widgets and
 * echoed raw UTC numbers as a clock ("2:06 AM" for a 10:06 PM EDT event).
 *
 * Contract: an offset-less datetime is treated as UTC. Strings that already carry
 * a 'Z' or ±HH:MM offset, date-only values, and Date objects pass through unchanged.
 */
export function parseDbInstant(value) {
  if (value == null || value === '') return new Date(NaN);
  if (value instanceof Date) return value;
  const str = String(value).trim();
  const hasTime = str.includes('T') || /\d{1,2}:\d{2}/.test(str);
  const hasTz = /[zZ]$/.test(str) || /[+-]\d{2}:?\d{2}$/.test(str);
  if (hasTime && !hasTz) {
    return new Date(`${str.replace(' ', 'T')}Z`);
  }
  return new Date(str);
}

function utcTodayYmd() {
  const n = new Date();
  const y = n.getUTCFullYear();
  const m = String(n.getUTCMonth() + 1).padStart(2, '0');
  const day = String(n.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Returns 'YYYY-MM-DD' for today in the given IANA timezone.
 */
export function tenantToday(tz) {
  const zone = safeIANA(tz);
  try {
    const parts = cachedFormatter('ymd', 'en-CA', zone, YMD_OPTIONS).formatToParts(new Date());
    const get = (type) => parts.find((p) => p.type === type).value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    return utcTodayYmd();
  }
}

/**
 * Returns a Date object set to midnight on the tenant's current calendar day.
 * Parsed as local midnight to stay consistent with how DB YYYY-MM-DD strings
 * are parsed throughout the app (new Date(`${str}T00:00:00`)).
 */
export function tenantTodayDate(tz) {
  try {
    return new Date(`${tenantToday(tz)}T00:00:00`);
  } catch {
    return new Date(`${utcTodayYmd()}T00:00:00`);
  }
}

/**
 * Returns a Date object for Monday 00:00:00 of the tenant's current week.
 */
export function tenantStartOfWeek(tz) {
  try {
    const today = tenantTodayDate(tz);
    const day = today.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    return monday;
  } catch {
    const t = tenantTodayDate('UTC');
    const day = t.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(t);
    monday.setDate(t.getDate() + diff);
    return monday;
  }
}

/**
 * Converts any date value to 'YYYY-MM-DD' in the given timezone.
 * Accepts a Date object, a YYYY-MM-DD string, or a timestamptz string.
 * Returns '' if value is falsy or unparseable.
 */
export function toTenantDateStr(value, tz) {
  if (!value) return '';
  const zone = safeIANA(tz);
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const parts = cachedFormatter('ymd', 'en-CA', zone, YMD_OPTIONS).formatToParts(d);
    const get = (type) => parts.find((p) => p.type === type).value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  } catch {
    return '';
  }
}

/**
 * The UTC instant of LOCAL midnight (00:00:00) on `dateYmd` in the given
 * tenant timezone — the inverse of `tenantToday`. Used for `.lte()`/`.gte()`
 * bounds against a `timestamptz` column where the boundary itself must be a
 * tenant-calendar-day edge, not a UTC one (e.g. "everything up through the
 * end of the tenant's local day").
 *
 * One-step convergence (the standard "guess an instant, read it back in the
 * target zone, correct by the delta" trick — see date-fns-tz's
 * zonedTimeToUtc): start from UTC midnight on `dateYmd`, format THAT instant
 * in `tz` to see what wall-clock it reads as there, then shift by the
 * difference. Accurate to the second outside a DST transition; a day-level
 * caller (this function's only use today) never needs a second iteration.
 *
 * @param {string} dateYmd 'YYYY-MM-DD'
 * @param {string} tz IANA timezone
 * @returns {string} ISO instant (always carries a 'Z')
 */
export function tenantMidnightUtcIso(dateYmd, tz) {
  const zone = safeIANA(tz);
  const guess = new Date(`${dateYmd}T00:00:00Z`);
  if (Number.isNaN(guess.getTime())) return guess.toISOString();
  try {
    const parts = cachedFormatter('full', 'en-US', zone, {
      hour12: false,
      ...YMD_OPTIONS,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(guess);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    // Intl's 24-hour format can print hour "24" for midnight in some engines —
    // normalize to 0.
    const hour = get('hour') === 24 ? 0 : get('hour');
    const readAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
    const deltaMs = guess.getTime() - readAsUtc;
    return new Date(guess.getTime() + deltaMs).toISOString();
  } catch {
    return guess.toISOString();
  }
}

/**
 * Calendar-day distance from fromYmd to toYmd (YYYY-MM-DD strings, UTC midnight math).
 * Positive when toYmd is after fromYmd.
 */
export function calendarDaysFromTo(fromYmd, toYmd) {
  if (!fromYmd || !toYmd) return 0;
  const parse = (s) => {
    const [y, m, d] = s.split('-').map(Number);
    return Date.UTC(y, m - 1, d);
  };
  try {
    return Math.round((parse(toYmd) - parse(fromYmd)) / 86400000);
  } catch {
    return 0;
  }
}
